import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TestimonialCard from './TestimonialCard';

const TestimonialsSection: React.FC = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animatedStats, setAnimatedStats] = useState({
    satisfaction: 0,
    cvAnalyzed: 0,
    rating: 0
  });

  const testimonials = t('landing.testimonials.testimonials', { returnObjects: true }) as Array<{
    text: string;
    author: string;
    role: string;
  }>;

  const gradients = [
    "from-pink-500 to-rose-600",
    "from-blue-500 to-cyan-600",
    "from-emerald-500 to-teal-600",
    "from-purple-500 to-indigo-600",
    "from-orange-500 to-red-600",
    "from-green-500 to-emerald-600",
    "from-cyan-500 to-blue-600",
    "from-violet-500 to-purple-600",
    "from-teal-500 to-cyan-600",
    "from-indigo-500 to-blue-600",
    "from-rose-500 to-pink-600",
    "from-amber-500 to-orange-600"
  ];

  const avatars = ["M", "T", "S", "A", "C", "J", "L", "N", "E", "M", "C", "P"];

  // Auto-swipe every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const maxIndex = Math.ceil(testimonials.length / 3) - 1;
        return prev >= maxIndex ? 0 : prev + 1;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Animate stats when component mounts
  useEffect(() => {
    const animateStats = () => {
      // Animate satisfaction percentage
      let satisfactionCount = 0;
      const satisfactionInterval = setInterval(() => {
        satisfactionCount += 2;
        setAnimatedStats(prev => ({ ...prev, satisfaction: Math.min(satisfactionCount, 98) }));
        if (satisfactionCount >= 98) clearInterval(satisfactionInterval);
      }, 30);

      // Animate CV analyzed count
      setTimeout(() => {
        let cvCount = 0;
        const cvInterval = setInterval(() => {
          cvCount += 300;
          setAnimatedStats(prev => ({ ...prev, cvAnalyzed: Math.min(cvCount, 15000) }));
          if (cvCount >= 15000) clearInterval(cvInterval);
        }, 20);
      }, 500);

      // Animate rating
      setTimeout(() => {
        let ratingCount = 0;
        const ratingInterval = setInterval(() => {
          ratingCount += 0.1;
          setAnimatedStats(prev => ({ ...prev, rating: Math.min(ratingCount, 4.9) }));
          if (ratingCount >= 4.9) clearInterval(ratingInterval);
        }, 40);
      }, 1000);
    };

    animateStats();
  }, []);

  // Get current testimonials to display (3 at a time)
  const getCurrentTestimonials = () => {
    const startIndex = currentIndex * 3;
    return testimonials.slice(startIndex, startIndex + 3);
  };

  // Calculate total pages
  const totalPages = Math.ceil(testimonials.length / 3);

  return (
    <section id="testimonials" className="py-20 bg-slate-900/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            {t('landing.testimonials.title').includes('Ils nous font') ? (
              <>
                Ils nous font{' '}
                <span className="bg-gradient-to-r from-pink-400 to-rose-400 bg-clip-text text-transparent">
                  confiance
                </span>{' '}
                <span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                  chaque jour
                </span>
              </>
            ) : (
              <>
                They trust us{' '}
                <span className="bg-gradient-to-r from-pink-400 to-rose-400 bg-clip-text text-transparent">
                  every
                </span>{' '}
                <span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                  day
                </span>
              </>
            )}
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            {t('landing.testimonials.subtitle').includes('utilisateurs satisfaits') ? (
              <>
                Découvrez les témoignages de nos{' '}
                <span className="text-pink-400 font-semibold">utilisateurs satisfaits</span>{' '}
                qui ont trouvé leur emploi idéal
              </>
            ) : (
              <>
                Discover testimonials from our{' '}
                <span className="text-pink-400 font-semibold">satisfied users</span>{' '}
                who found their ideal job
              </>
            )}
          </p>
        </div>
        
        {/* Testimonials Grid - 3 at a time */}
        <div className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-500 ease-in-out">
            {getCurrentTestimonials().map((testimonial, index) => {
              const globalIndex = currentIndex * 3 + index;
              return (
                <TestimonialCard
                  key={`${currentIndex}-${index}`}
                  text={testimonial.text}
                  author={testimonial.author}
                  role={testimonial.role}
                  avatar={avatars[globalIndex]}
                  gradient={gradients[globalIndex]}
                  delay={index * 100}
                />
              );
            })}
          </div>
          
          {/* Pagination Dots */}
          <div className="flex justify-center mt-12 space-x-3">
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 scale-125'
                    : 'bg-slate-600 hover:bg-slate-500'
                }`}
              />
            ))}
          </div>
        </div>
        
        {/* Stats without frames */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
          <div className="group">
            <div className="text-6xl font-black bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform duration-300">
              {animatedStats.satisfaction}%
            </div>
            <p className="text-slate-300 text-lg font-medium">{t('landing.testimonials.stats.satisfaction_rate')}</p>
          </div>
          <div className="group">
            <div className="text-6xl font-black bg-gradient-to-r from-blue-400 to-cyan-500 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform duration-300">
              {animatedStats.cvAnalyzed >= 1000 ? `${Math.floor(animatedStats.cvAnalyzed / 1000)}k+` : animatedStats.cvAnalyzed}
            </div>
            <p className="text-slate-300 text-lg font-medium">{t('landing.testimonials.stats.cv_analyzed')}</p>
          </div>
          <div className="group">
            <div className="text-6xl font-black bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform duration-300">
              {animatedStats.rating.toFixed(1)}/5
            </div>
            <p className="text-slate-300 text-lg font-medium">{t('landing.testimonials.stats.average_rating')}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;